import {
  action,
  makeObservable,
  observable,
  onBecomeObserved,
  onBecomeUnobserved
} from 'mobx';

export const SLA_TICK_INTERVAL_MS = 1000;

/**
 * Один источник времени на всё приложение.
 *
 * В списке диалогов таймеров десятки, и свой `setInterval` в каждой строке —
 * это столько же интервалов и столько же ререндеров в секунду. Здесь интервал
 * один, а живёт он ровно пока `now` кто-то читает: `onBecomeObserved` заводит
 * его, `onBecomeUnobserved` гасит. Приостановленный таймер `now` не читает,
 * поэтому сам собой из тикера выпадает.
 *
 * На скрытой вкладке тикер останавливается: браузер всё равно душит таймеры, а
 * возврат вкладки сразу подтягивает актуальное время — дрейф не копится.
 */
export class SlaClockModel {
  private _now: number = Date.now();
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private isObserved = false;

  constructor() {
    makeObservable<SlaClockModel, '_now' | 'setNow'>(this, {
      _now: observable,
      setNow: action
    });

    onBecomeObserved(this, '_now', this.handleBecomeObserved);
    onBecomeUnobserved(this, '_now', this.handleBecomeUnobserved);
  }

  get now(): number {
    return this._now;
  }

  private setNow(value: number): void {
    this._now = value;
  }

  private handleBecomeObserved = (): void => {
    this.isObserved = true;

    // Синглтон создаётся при импорте модуля и до первого тика держит время
    // той минуты. Первый же наблюдатель должен видеть актуальное, а не ждать
    // секунду на устаревшем.
    this.setNow(Date.now());

    if (typeof document !== 'undefined') {
      document.addEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );
    }

    this.start();
  };

  private handleBecomeUnobserved = (): void => {
    this.isObserved = false;

    if (typeof document !== 'undefined') {
      document.removeEventListener(
        'visibilitychange',
        this.handleVisibilityChange
      );
    }

    this.stop();
  };

  private handleVisibilityChange = (): void => {
    if (document.hidden) {
      this.stop();

      return;
    }

    // Возврат на вкладку: сначала актуальное время, потом обычный ход часов.
    this.setNow(Date.now());
    this.start();
  };

  private start(): void {
    if (this.intervalId !== null || !this.isObserved || document?.hidden) {
      return;
    }

    this.intervalId = setInterval(() => {
      this.setNow(Date.now());
    }, SLA_TICK_INTERVAL_MS);
  }

  private stop(): void {
    if (this.intervalId === null) {
      return;
    }

    clearInterval(this.intervalId);
    this.intervalId = null;
  }
}

/** Общий экземпляр для UI; в тестах создаётся свой. */
export const slaClock = new SlaClockModel();
